import { Check, Eye } from 'lucide-react';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { LocalTime } from '~/components/common/LocalTime';
import { ListToolbar } from '~/components/layout';
import { ComboField } from '~/components/form';
import {
  Badge,
  Button,
  Card,
  CardBody,
  DataTable,
  EmptyState,
  FilterDrawer,
  HasMorePagination,
  RowActionsMenu,
  StatusChip,
  Tabs,
  type DataTableColumn,
} from '~/components/ui';
import { useDebouncedQuery } from '~/hooks/useDebouncedQuery';
import { useListUrlState } from '~/hooks/useListUrlState';
import { useUrlParams } from '~/hooks/useUrlParams';

import {
  REQUEST_ORDERS,
  REQUEST_PAGE_SIZE,
  REQUEST_STATUSES,
  REQUEST_STATUS_LABELS,
  REQUEST_STATUS_TONES,
} from '~/components/Constants/featureFlagsConstants';
import {
  errorText,
  isStale,
  orDash,
  useLegacyOffset,
} from '~/components/pages/feature-flags/common';
import type { Reference } from '~/components/pages/feature-flags/common';
import type { FeatureRequest, RequestStatus } from '~/components/pages/feature-flags/types';
import { useListRequestsQuery, useModerateRequestMutation } from '~/modules/query/featureFlags';
import { routes } from '~/app/routes';

type Order = (typeof REQUEST_ORDERS)[number]['id'];

const FILTER_KEYS = ['product', 'status'] as const;
const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...REQUEST_STATUSES.map((s) => ({ value: s, label: REQUEST_STATUS_LABELS[s] })),
];

// The service pages requests by offset with a fixed 20 and returns `hasMore`, no total: the URL carries `page`, the query sends `offset = (page - 1) * 20`.
export const RequestsTab = ({ reference }: { reference: Reference }) => {
  const url = useUrlParams();
  const { products } = reference;
  const list = useListUrlState({
    filterKeys: FILTER_KEYS,
    pageSizes: [REQUEST_PAGE_SIZE],
    defaultPageSize: REQUEST_PAGE_SIZE,
  });
  const migrating = useLegacyOffset(REQUEST_PAGE_SIZE);
  const [showFilters, setShowFilters] = useState(false);
  const [moderateRequest] = useModerateRequestMutation();
  const product =
    products.find((p) => p.key === list.filters.product)?.key ?? products[0]?.key ?? '';
  const recordHref = (id: string) =>
    `${routes.featureFlagsRecord('requests', id)}?product=${encodeURIComponent(product)}`;
  const productName = products.find((p) => p.key === product)?.name ?? product;
  const order: Order = url.get('order') === 'votes' ? 'votes' : 'latest';
  const status = (REQUEST_STATUSES as readonly string[]).includes(list.filters.status)
    ? (list.filters.status as RequestStatus)
    : '';
  const query = useDebouncedQuery(list.q.trim().slice(0, 200));

  const result = useListRequestsQuery(
    { product, query, order, status, offset: (list.page - 1) * REQUEST_PAGE_SIZE },
    { skip: !product || migrating },
  );

  const approve = (r: FeatureRequest) => {
    moderateRequest({
      id: r.id,
      data: { product, version: r.version, status: 'approved', hidden: r.hidden },
    })
      .unwrap()
      .then(() => toast.success('Request approved'))
      .catch((err) =>
        toast.error(
          isStale(err)
            ? 'This request was changed by someone else. Reload and try again.'
            : errorText(err, 'The request could not be approved. Try again.'),
        ),
      );
  };

  const columns: DataTableColumn<FeatureRequest>[] = [
    {
      id: 'votes',
      header: 'Votes',
      cell: (r) => (
        <span className="font-medium text-gray-800 dark:text-white/90">{r.voteCount}</span>
      ),
    },
    {
      id: 'request',
      header: 'Request',
      cell: (r) => (
        <div className="max-w-md space-y-1">
          <p className="font-medium text-gray-800 dark:text-white/90">{r.title}</p>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400" title={r.description}>
            {r.description}
          </p>
          {r.hidden ? (
            <Badge size="sm" color="warning">
              Hidden from users
            </Badge>
          ) : (
            !r.approvedAt && (
              <Badge size="sm" color="gray">
                Awaiting approval
              </Badge>
            )
          )}
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => (
        <StatusChip
          label={REQUEST_STATUS_LABELS[r.status]}
          tone={REQUEST_STATUS_TONES[r.status]}
          dot
        />
      ),
    },
    { id: 'enhancements', header: 'Enhancements', cell: (r) => orDash(r.enhancementCount) },
    { id: 'submitted', header: 'Submitted', cell: (r) => <LocalTime value={r.createdAt} /> },
    {
      id: 'actions',
      header: 'Actions',
      cell: (r) => (
        <RowActionsMenu
          testId={`feature-flags-requests-actions-${r.id}`}
          items={[
            {
              label: 'Review',
              testId: 'open',
              icon: <Eye className="size-4" aria-hidden="true" />,
              href: recordHref(r.id),
            },
            {
              label: 'Approve',
              testId: 'approve',
              disabled: !!r.approvedAt,
              icon: <Check className="size-4" aria-hidden="true" />,
              onClick: () => approve(r),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <Card>
        <ListToolbar
          title="Feature requests"
          description={productName}
          search={{
            value: list.q,
            onChange: list.setQ,
            placeholder: 'Search feature requests…',
            testId: 'feature-flags-requests-search',
          }}
          filters={{
            count: list.filterCount,
            onClick: () => setShowFilters(true),
            onClear: list.clear,
            testId: 'feature-flags-requests-filter-button',
          }}
          actions={
            <Tabs
              variant="pill"
              ariaLabel="Request order"
              idPrefix="feature-flags-requests-order"
              activeId={order}
              onChange={(id) => url.set({ order: id === 'latest' ? null : id, page: null })}
              tabs={REQUEST_ORDERS.map((o) => ({ id: o.id, label: o.label }))}
            />
          }
        />
        {products.length === 0 ? (
          <CardBody>
            <EmptyState
              title="Add a product first"
              description="Feature requests belong to a product. Add one on the Products page to start collecting ideas."
              action={
                <Button variant="outline" href={routes.featureFlagsAdmin('products')}>
                  Go to products
                </Button>
              }
            />
          </CardBody>
        ) : (
          <div
            role="tabpanel"
            id={`feature-flags-requests-order-panel-${order}`}
            aria-labelledby={`feature-flags-requests-order-tab-${order}`}
          >
            <CardBody className="p-0">
              <DataTable<FeatureRequest>
                framed={false}
                testId="feature-flags-requests-table"
                loading={!result.data && !result.error}
                refreshing={result.isFetching && !!result.data}
                error={
                  result.error && !result.data
                    ? {
                        what: 'the feature requests',
                        message: apiErrorMessage(result.error as ApiErrorInput),
                        onRetry: result.refetch,
                      }
                    : undefined
                }
                emptyMessage="No requests match this view yet."
                emptyDescription="Requests appear when customers submit ideas from the product drawer."
                data={result.data?.requests ?? []}
                rowKey={(r) => r.id}
                href={(r) => recordHref(r.id)}
                rowLabel={(r) => `Open ${r.title}`}
                columns={columns}
              />
            </CardBody>
            <HasMorePagination
              pageIndex={list.page - 1}
              hasMore={!!result.data?.hasMore}
              onPageChange={(index) => list.setPage(index + 1)}
            />
          </div>
        )}
      </Card>

      <FilterDrawer<Record<string, string>>
        open={showFilters}
        onClose={() => setShowFilters(false)}
        value={{ product, status }}
        onApply={list.setFilters}
        onClear={list.clear}
        testId="feature-flags-requests-filter-drawer"
      >
        {(draft, patch) => (
          <>
            <ComboField
              id="feature-flags-requests-filter-product"
              testId="feature-flags-requests-filter-product-select"
              label="Product"
              value={draft.product}
              options={products.map((p) => ({ value: p.key, label: p.name }))}
              searchable={false}
              onChange={(next) => patch({ product: next })}
            />
            <ComboField
              id="feature-flags-requests-filter-status"
              testId="feature-flags-requests-filter-status-select"
              label="Status"
              value={draft.status}
              options={STATUS_OPTIONS}
              searchable={false}
              onChange={(next) => patch({ status: next })}
            />
          </>
        )}
      </FilterDrawer>
    </>
  );
};

export default RequestsTab;
